import React, { useMemo } from "react";
import {
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import CommonTableIndexWise, { Column } from "Components/Common/CommonTableIndexWise";
import TruncatedText from "Components/Common/TruncatedText";
import { User, UserDepartment } from "../types/user.types";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  selectedUser?: { user?: User; department?: UserDepartment[] } | null;
  loading?: boolean;
}

const ViewUser: React.FC<Props> = ({
  isOpen,
  toggle,
  selectedUser,
  loading = false,
}) => {
  const user = selectedUser?.user;
  const departments = selectedUser?.department || [];

  const departmentColumns: Column<UserDepartment>[] = useMemo(
    () => [
      {
        key: "branname",
        label: "Branch",
        render: (_: any, row?: UserDepartment) => (
          <span className="d-inline-flex align-items-center">
            {row?.branname || "-"}
            {row?.isdefault && (
              <span className="badge bg-primary ms-2">Default</span>
            )}
          </span>
        ),
      },
      {
        key: "department",
        label: "Department",
        render: (val: any) => val || "-",
      },
    ],
    [],
  );

  const renderField = (label: string, value?: string | number | null) => (
    <div>
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-semibold text-break">{value || "-"}</div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static"
      centered
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        {user?.username && !loading ? (
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            View User
            <TruncatedText text={`(${user.username})`} maxWidth="400px" />
          </span>
        ) : (
          "View User"
        )}
      </ModalHeader>

      <ModalBody>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <div className="row g-3">
              <div className="col-md-6">
                {renderField("User Name", user?.username)}
              </div>
              <div className="col-md-6">
                {renderField("Full Name", user?.userdisplayname)}
              </div>
              <div className="col-md-6">
                {renderField("Employee Code", user?.empcode)}
              </div>
              <div className="col-md-6">
                {renderField("Email", user?.useremail)}
              </div>
              <div className="col-md-6">
                {renderField("Branch", user?.branname)}
              </div>
              <div className="col-md-6">
                {renderField("Department", user?.department)}
              </div>
              <div className="col-md-6">
                {renderField("Designation", user?.designation)}
              </div>
              <div className="col-md-6">
                {renderField(
                  "Allow authentication with AD?",
                  user?.isldap ? "Yes" : "No",
                )}
              </div>
              <div className="col-12">
                {renderField("Locations", user?.userlocationnames)}
              </div>
            </div>

            {/* Applicable Department */}
            <div className="mt-4">
              <h6 className="fw-bold mb-3">Applicable Department</h6>

              <CommonTableIndexWise
                columns={departmentColumns}
                data={departments}
                paginationShow={false}
                hideAction={true}
                showStatus={false}
                emptyText="No Record Found"
              />
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="dark" outline onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ViewUser;
